import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Boxes,
  Calendar,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Menu,
  Receipt,
  Search,
  Settings,
  ShoppingCart,
  Square,
  Star,
  Ticket,
  Timer,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.js';
import { messagingApi } from '@/lib/api/messaging.js';
import { inquiriesApi } from '@/lib/api/inquiries.js';
import { appointmentsApi } from '@/lib/api/appointments.js';
import { apiGet } from '@/lib/api/client.js';
import { getOpenPartsRequestCount } from '@/lib/api/parts-requests.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { patchTimeEntry } from '@/lib/api/tickets.js';
import { cashSessionsApi } from '@/lib/api/cash-sessions.js';
import { formatCurrency } from '@/lib/format.js';
import { CashDrawerModal } from '@/components/cash/CashDrawerModal.js';
import { CommandPalette } from '@/components/CommandPalette.js';
import { NotificationBell } from '@/components/notifications/NotificationBell.js';
import { UserMenuDropdown } from '@/components/layout/UserMenuDropdown.js';
import { useDisplayStaffBridge } from '@/hooks/useDisplayStaffBridge.js';
import { useRunningTimerClock } from '@/hooks/useRunningTimerClock.js';
import { clearRunningTimer, formatClockSeconds } from '@/lib/running-timer-storage.js';
import { ticketDetailPath } from '@/lib/ticket-detail-path.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useUserPreferences } from '@/contexts/UserPreferencesContext.js';
import { ChecklistBanner, useChecklistHeaderBadge } from '@/components/checklists/ChecklistBanner.js';
import { ChecklistSlideOver } from '@/components/checklists/ChecklistSlideOver.js';
import { StoreSwitcher } from '@/components/layout/StoreSwitcher.js';
import { overrideRequestsApi } from '@/lib/api/override-requests.js';
import { checklistProgressColor } from '@/lib/checklist-defaults.js';
import type { ChecklistType } from '@/lib/api/checklists.js';
import { wsManager } from '@/lib/websocket-manager.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { useMultiStore } from '@/hooks/useMultiStore.js';
import { isManagerRole } from '@/lib/permissions.js';
import { WalkInQueueHeaderChip } from '@/pages/queue/WalkInQueuePage.js';
import { walkInQueueApi } from '@/lib/api/walk-in-queue.js';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/tech', label: 'My Work', icon: Wrench },
  { to: '/tickets', label: 'Tickets', icon: Ticket },
  { to: '/queue', label: 'Walk-in', icon: Users, badgeKey: 'walkIn' as const },
  { to: '/inquiries', label: 'Inquiries', icon: ClipboardList, badgeKey: 'inquiries' as const },
  { to: '/appointments', label: 'Appointments', icon: Calendar, badgeKey: 'appointments' as const },
  { to: '/messages', label: 'Messages', icon: MessageSquare, badgeKey: 'messages' as const },
  { to: '/pos', label: 'POS', icon: ShoppingCart },
  { to: '/sales', label: 'Sales History', icon: Receipt },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/reviews', label: 'Reviews', icon: Star },
  { to: '/settings', label: 'Settings', icon: Settings },
];

function titleFromPath(pathname: string): string {
  if (pathname === '/reports' || pathname === '/reports/') return 'Reports';
  if (pathname.startsWith('/reports/')) {
    const seg = pathname.split('/')[2] ?? '';
    const map: Record<string, string> = {
      revenue: 'Revenue',
      tickets: 'Tickets',
      technicians: 'Technicians',
      repairs: 'Repairs',
      inventory: 'Inventory',
      'multi-store': 'Multi-store',
      'end-of-day': 'End of day',
      insurance: 'Insurance',
      'rma-and-rework': 'RMA and rework',
    };
    return map[seg] ?? 'Reports';
  }
  if (pathname.startsWith('/inventory/purchase-orders')) return 'Purchase Orders';
  if (pathname.startsWith('/inventory/parts-requests')) return 'Parts Requests';
  if (pathname === '/tech') return 'My Work';
  if (pathname === '/messages') return 'Messages';
  if (pathname === '/inquiries' || pathname.startsWith('/inquiries/')) return 'Inquiries';
  if (pathname === '/appointments') return 'Appointments';
  if (pathname === '/queue') return 'Walk-in Queue';
  if (pathname === '/reviews') return 'Reviews';
  if (pathname === '/sales' || pathname.startsWith('/sales/')) return 'Sales History';
  if (pathname.startsWith('/inventory/counts')) return 'Cycle Counts';
  if (pathname.startsWith('/inventory/transfers')) return 'Transfers';
  if (pathname.startsWith('/inventory/rmas')) return 'Supplier RMAs';
  if (pathname.startsWith('/inventory/damage-reports')) return 'Damage Reports';
  if (pathname === '/subscriptions') return 'Subscriptions';
  if (pathname === '/inventory/new') return 'New inventory item';
  if (/^\/inventory\/[0-9a-f-]{36}$/i.test(pathname)) return 'Inventory Item';
  const match = navItems.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  if (match) return match.label;
  if (pathname === '/inventory' || pathname.startsWith('/inventory/')) return 'Inventory';
  return 'Dashboard';
}

function canUseCashDrawer(role?: string) {
  return ['front_desk', 'store_manager', 'tenant_admin', 'platform_admin'].includes(role ?? '');
}

function ApprovalPendingBanner() {
  const user = useAuthStore((s) => s.user);
  const enabled = isManagerRole(user?.role ?? 'read_only');
  const { data } = useQuery({
    queryKey: ['override-pending-count'],
    queryFn: () => overrideRequestsApi.pendingCount(),
    enabled,
    refetchInterval: 15_000,
  });
  const count = data?.count ?? 0;
  if (!enabled || count < 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-950 sm:px-6 lg:px-8">
      <span>
        ⚠ {count} approval request{count === 1 ? '' : 's'} pending
      </span>
      <Link
        to="/approvals"
        className="rounded-lg bg-amber-900 px-3 py-1 text-xs font-bold text-white hover:bg-amber-800"
      >
        Review
      </Link>
    </div>
  );
}

export function AppLayout() {
  const [open, setOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);
  const [quoteSearchOpen, setQuoteSearchOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistType, setChecklistType] = useState<ChecklistType>('open');
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { profile, preferences } = useUserPreferences();
  const { can: canPerm } = usePermissions();
  const canViewDashboard = canPerm('view_dashboard');
  const { isMultiStore } = useMultiStore();
  useDisplayStaffBridge(user?.storeId);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [openPartsRequests, setOpenPartsRequests] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [overdueInquiries, setOverdueInquiries] = useState(0);
  const [appointmentsToday, setAppointmentsToday] = useState(0);
  const [walkInWaiting, setWalkInWaiting] = useState(0);
  const [liveUpdatesUnavailable, setLiveUpdatesUnavailable] = useState(false);
  const pageTitle = useMemo(() => titleFromPath(location.pathname), [location.pathname]);
  const storeName = profile?.storeName ?? (user?.storeId ? `Store ${user.storeId.slice(0, 8)}` : 'Current Store');
  const showCashDrawer = canUseCashDrawer(user?.role);
  const canPurchase = ['store_manager', 'tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const { payload: runningTimer, elapsed: runningElapsed } = useRunningTimerClock();
  const showGlobalRunningTimer =
    Boolean(runningTimer) && location.pathname !== `/tickets/${runningTimer?.ticketId}`;
  const storeQuery = useQuery({
    queryKey: ['store-current'],
    queryFn: getCurrentStore,
    staleTime: 60_000,
  });
  const queueEnabled = Boolean(storeQuery.data?.queueEnabled);
  const commsDryRun = Boolean(storeQuery.data?.commsDryRun);
  const openingChecklistEnabled = Boolean(storeQuery.data?.openingChecklistEnabled);
  const closingChecklistEnabled = Boolean(storeQuery.data?.closingChecklistEnabled);
  const anyChecklistEnabled = openingChecklistEnabled || closingChecklistEnabled;
  const { type: headerChecklistType, progress: checklistProgress } = useChecklistHeaderBadge({
    openingEnabled: openingChecklistEnabled,
    closingEnabled: closingChecklistEnabled,
  });

  const stopRunningTimerMut = useMutation({
    mutationFn: () =>
      patchTimeEntry(runningTimer!.ticketId, runningTimer!.entryId, {
        endedAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      clearRunningTimer();
      if (runningTimer?.ticketId) {
        queryClient.invalidateQueries({ queryKey: ['ticket', runningTimer.ticketId] });
      }
    },
    onError: (e) => toastError(parseApiError(e).message),
  });
  const cashQuery = useQuery({
    queryKey: ['cash-session-current'],
    queryFn: cashSessionsApi.getCurrent,
    enabled: showCashDrawer,
    refetchInterval: 300_000,
  });

  useEffect(() => {
    let mounted = true;
    async function loadLowStockCount() {
      if (!canViewDashboard) {
        if (mounted) setLowStockCount(0);
        return;
      }
      try {
        const res = await apiGet<{ lowStockCount: number }>('/api/v1/reports/dashboard');
        if (mounted) setLowStockCount(Number(res.data.lowStockCount ?? 0));
      } catch {
        if (mounted) setLowStockCount(0);
      }
    }

    loadLowStockCount();
    const interval = window.setInterval(loadLowStockCount, 5 * 60 * 1000);
    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, [canViewDashboard]);

  useEffect(() => {
    let mounted = true;
    async function loadOpenParts() {
      if (!canPurchase) {
        if (mounted) setOpenPartsRequests(0);
        return;
      }
      try {
        const res = await getOpenPartsRequestCount();
        if (mounted) setOpenPartsRequests(Number(res.openCount ?? 0));
      } catch {
        if (mounted) setOpenPartsRequests(0);
      }
    }
    void loadOpenParts();
    const interval = window.setInterval(() => void loadOpenParts(), 60_000);
    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, [canPurchase]);

  useEffect(() => {
    let mounted = true;
    async function loadUnread() {
      try {
        const res = await messagingApi.unreadCount();
        if (mounted) setUnreadMessages(Number(res.data.unreadCount ?? 0));
      } catch {
        if (mounted) setUnreadMessages(0);
      }
    }
    loadUnread();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadOverdueInquiries() {
      try {
        const res = await inquiriesApi.stats();
        if (mounted) setOverdueInquiries(Number(res.data.overdueFollowUps ?? 0));
      } catch {
        if (mounted) setOverdueInquiries(0);
      }
    }
    loadOverdueInquiries();
    const interval = window.setInterval(loadOverdueInquiries, 60_000);
    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadWalkIn() {
      if (!queueEnabled) {
        if (mounted) setWalkInWaiting(0);
        return;
      }
      try {
        const s = await walkInQueueApi.status();
        if (mounted) setWalkInWaiting(s.enabled ? s.waitingCount : 0);
      } catch {
        if (mounted) setWalkInWaiting(0);
      }
    }
    loadWalkIn();
    const interval = window.setInterval(loadWalkIn, 15_000);
    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, [queueEnabled]);

  useEffect(() => {
    let mounted = true;
    async function loadAppointmentsToday() {
      try {
        const count = await appointmentsApi.todayCount();
        if (mounted) setAppointmentsToday(count);
      } catch {
        if (mounted) setAppointmentsToday(0);
      }
    }
    loadAppointmentsToday();
    const interval = window.setInterval(loadAppointmentsToday, 60_000);
    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const base = 'Repairiz';
    document.title = unreadMessages > 0 ? `(${unreadMessages}) ${base}` : base;
  }, [unreadMessages]);

  useEffect(() => {
    if (!user?.storeId) return;

    wsManager.connect(`store:${user.storeId}`, `/ws/store/${user.storeId}`);
    wsManager.connect(`store:${user.storeId}:messages`, `/ws/messages/${user.storeId}`);
    wsManager.connect(`store:${user.storeId}:alerts`, `/ws/alerts/${user.storeId}`);

    const unsubStatus = wsManager.onStatus((status) => {
      if (status === 'failed' || wsManager.hasFailedChannel()) {
        setLiveUpdatesUnavailable(true);
      } else if (status === 'open') {
        setLiveUpdatesUnavailable(wsManager.hasFailedChannel());
      }
    });

    const unsubStore = wsManager.on(`store:${user.storeId}`, (data) => {
      if (data.type === 'queue.counts_updated') {
        queryClient.setQueryData(['ticket-counts'], data.payload);
      }
      if (data.type === 'payment.recorded') {
        queryClient.invalidateQueries({ queryKey: ['dashboard-live'] });
        queryClient.invalidateQueries({ queryKey: ['ticket-counts'] });
      }
      if (data.type === 'walk_in_queue.updated') {
        const payload = data.payload as { waitingCount?: number };
        if (typeof payload?.waitingCount === 'number') {
          setWalkInWaiting(payload.waitingCount);
        }
        queryClient.invalidateQueries({ queryKey: ['walk-in-queue'] });
        queryClient.invalidateQueries({ queryKey: ['walk-in-queue-status'] });
      }
    });

    const unsubMessages = wsManager.on(`store:${user.storeId}:messages`, (data) => {
      if (data.type === 'message.received') {
        void messagingApi.unreadCount().then((res) => {
          setUnreadMessages(Number(res.data.unreadCount ?? 0));
        });
      }
    });

    const unsubAlerts = wsManager.on(`store:${user.storeId}:alerts`, (data) => {
      try {
        const msg = data as {
          type?: string;
          payload?: Record<string, unknown>;
        };
        if (msg.type === 'estimate.approved') {
          toastSuccess(
            `${msg.payload?.customerName ?? 'Customer'} approved ${msg.payload?.estimateNumber ?? 'estimate'} ($${msg.payload?.total ?? ''}) — ${msg.payload?.ticketNumber ?? ''}`,
          );
        } else if (msg.type === 'estimate.declined') {
          toastError(
            `${msg.payload?.customerName ?? 'Customer'} declined ${msg.payload?.estimateNumber ?? 'estimate'} — ${msg.payload?.ticketNumber ?? ''}`,
          );
        } else if (msg.type === 'ticket.customer_note') {
          toastSuccess(
            `${msg.payload?.customerName ?? 'Customer'} added a note to ${msg.payload?.ticketNumber ?? 'a ticket'}`,
          );
        } else if (msg.type === 'ticket.portal_request') {
          toastSuccess(
            `${msg.payload?.customerName ?? 'Customer'} submitted a new repair request — pending review`,
          );
        }
      } catch {
        /* ignore */
      }
    });

    return () => {
      unsubStatus();
      unsubStore();
      unsubMessages();
      unsubAlerts();
    };
  }, [user?.storeId, queryClient]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setQuoteSearchOpen(true);
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        navigate('/tickets/quick');
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        navigate('/pos');
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'i') {
        event.preventDefault();
        navigate('/inquiries/new');
      }
      if (event.key === 'Escape') {
        setQuoteSearchOpen(false);
        setChecklistOpen(false);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  function openChecklist(type: ChecklistType) {
    setChecklistType(type);
    setChecklistOpen(true);
  }

  const sidebar = (
    <aside className="flex h-full w-72 flex-col border-r border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5 dark:border-slate-700">
        <div className="text-xl font-bold tracking-tight">
          <span className="text-blue-500">Repair</span>
          <span className="text-slate-900 dark:text-white">iz</span>
        </div>
        <button
          type="button"
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"
          onClick={() => setOpen(false)}
          aria-label="Close navigation"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-5">
        {navItems
          .filter((item) => {
            if (item.to === '/dashboard') return canViewDashboard;
            if (item.to === '/reports') return canPerm('view_reports');
            if (item.to === '/queue') return queueEnabled;
            return true;
          })
          .map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition',
                  isActive
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
                ].join(' ')
              }
            >
              <Icon className="h-5 w-5" />
              <span className="flex-1">{item.label}</span>
              {'badgeKey' in item && item.badgeKey === 'messages' && unreadMessages > 0 ? (
                <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                  {unreadMessages}
                </span>
              ) : null}
              {'badgeKey' in item && item.badgeKey === 'inquiries' && overdueInquiries > 0 ? (
                <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                  {overdueInquiries}
                </span>
              ) : null}
              {'badgeKey' in item && item.badgeKey === 'appointments' && appointmentsToday > 0 ? (
                <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white">
                  {appointmentsToday}
                </span>
              ) : null}
              {'badgeKey' in item && item.badgeKey === 'walkIn' && walkInWaiting > 0 ? (
                <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-white">
                  {walkInWaiting}
                </span>
              ) : null}
            </NavLink>
          );
        })}

        <div className="pt-1">
          <NavLink
            to="/inventory"
            onClick={() => setOpen(false)}
            className={() => {
              const invActive =
                location.pathname === '/inventory' || location.pathname.startsWith('/inventory/');
              return [
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition',
                invActive ? 'bg-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950',
              ].join(' ');
            }}
          >
            <Boxes className="h-5 w-5" />
            <span className="flex-1">Inventory</span>
            {canPurchase && openPartsRequests > 0 ? (
              <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-white" title="Open parts requests">
                {openPartsRequests}
              </span>
            ) : null}
            {lowStockCount > 0 ? (
              <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                {lowStockCount}
              </span>
            ) : null}
          </NavLink>
          {location.pathname.startsWith('/inventory') ? (
            <div className="ml-2 mt-1 space-y-0.5 border-l border-slate-200 py-1 pl-3">
              <NavLink
                to="/inventory"
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                Items
              </NavLink>
              {canPurchase ? (
              <NavLink
                to="/inventory/parts-requests"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'flex items-center justify-between rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                <span>Parts Requests</span>
                {openPartsRequests > 0 ? (
                  <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-white">{openPartsRequests}</span>
                ) : null}
              </NavLink>
              ) : null}
              <NavLink
                to="/inventory/purchase-orders"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                Purchase Orders
              </NavLink>
              <NavLink
                to="/inventory/counts"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                Cycle Counts
              </NavLink>
              <NavLink
                to="/inventory/rmas"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                RMAs
              </NavLink>
              <NavLink
                to="/inventory/damage-reports"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                Damage Reports
              </NavLink>
              {isMultiStore ? (
                <NavLink
                  to="/inventory/transfers"
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    [
                      'block rounded-lg px-2 py-1.5 text-sm font-medium',
                      isActive
                        ? 'bg-slate-100 text-slate-950'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                    ].join(' ')
                  }
                >
                  Transfers
                </NavLink>
              ) : null}
              <NavLink
                to="/settings/suppliers"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                Suppliers
              </NavLink>
            </div>
          ) : null}
        </div>

        <div className="pt-1">
          <NavLink
            to="/customers"
            onClick={() => setOpen(false)}
            className={() => {
              const customersActive =
                location.pathname === '/customers' ||
                location.pathname.startsWith('/customers/') ||
                location.pathname === '/subscriptions';
              return [
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition',
                customersActive
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
              ].join(' ');
            }}
          >
            <Users className="h-5 w-5" />
            <span className="flex-1">Customers</span>
          </NavLink>
          {location.pathname.startsWith('/customers') || location.pathname === '/subscriptions' ? (
            <div className="ml-2 mt-1 space-y-0.5 border-l border-slate-200 py-1 pl-3">
              <NavLink
                to="/customers"
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                All Customers
              </NavLink>
              <NavLink
                to="/subscriptions"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  [
                    'block rounded-lg px-2 py-1.5 text-sm font-medium',
                    isActive ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  ].join(' ')
                }
              >
                Subscriptions
              </NavLink>
            </div>
          ) : null}
        </div>
      </nav>

      <div className="border-t border-slate-200 p-4 dark:border-slate-700">
        <Link
          to="/settings/profile"
          onClick={() => setOpen(false)}
          className="mb-3 block rounded-xl bg-slate-50 p-3 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
        >
          <div className="text-sm font-semibold text-slate-900 dark:text-white">
            {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User'}
          </div>
          <div className="text-xs capitalize text-slate-500 dark:text-slate-400">
            {user?.role?.replace(/_/g, ' ')}
          </div>
        </Link>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          onClick={() => clearAuth()}
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );

  const isPosFullscreen = location.pathname === '/pos';

  if (isPosFullscreen) {
    return (
      <>
        <Outlet />
        <CommandPalette open={quoteSearchOpen} onClose={() => setQuoteSearchOpen(false)} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:block">{sidebar}</div>

      {open ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation overlay"
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute inset-y-0 left-0">{sidebar}</div>
        </div>
      ) : null}

      <div className="lg:pl-72">
        {liveUpdatesUnavailable ? (
          <div
            className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm font-medium text-amber-950 sm:px-6 lg:px-8"
            role="status"
          >
            Live updates unavailable — refresh the page to retry
          </div>
        ) : null}
        <ApprovalPendingBanner />
        {commsDryRun ? (
          <div
            className="border-b border-amber-400 bg-amber-200 px-4 py-2.5 text-center text-sm font-bold text-amber-950 sm:px-6 lg:px-8"
            role="status"
          >
            ⚠ DRY RUN MODE — SMS and email are not being sent
          </div>
        ) : null}
        <ChecklistBanner
          onOpen={openChecklist}
          openingEnabled={openingChecklistEnabled}
          closingEnabled={closingChecklistEnabled}
        />
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur dark:border-slate-700 dark:bg-slate-900/90 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 lg:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-slate-950 dark:text-white">{pageTitle}</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">{storeName}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {anyChecklistEnabled ? (
              <button
                type="button"
                onClick={() => openChecklist(headerChecklistType)}
                className={[
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold ring-1',
                  checklistProgressColor(checklistProgress.completed, checklistProgress.total),
                ].join(' ')}
                title="Daily checklist"
              >
                📋 [{checklistProgress.completed}/{checklistProgress.total || '—'}]
              </button>
            ) : null}
            <WalkInQueueHeaderChip />
            <NotificationBell />
            <button
              type="button"
              onClick={() => setQuoteSearchOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              title="Command palette (⌘K / Ctrl+K)"
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">Search</span>
            </button>
            {showGlobalRunningTimer ? (
              <div className="flex flex-wrap items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-950">
                <Timer className="h-3.5 w-3.5 shrink-0" />
                <Link
                  to={ticketDetailPath(runningTimer!.ticketId, {
                    role: user?.role,
                    pref: preferences?.ticketDetailView,
                  })}
                  className="hover:underline"
                >
                  {runningTimer!.ticketNumber}
                </Link>
                <span className="font-mono">{formatClockSeconds(runningElapsed)}</span>
                <button
                  type="button"
                  disabled={stopRunningTimerMut.isPending}
                  onClick={() => stopRunningTimerMut.mutate()}
                  className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white px-2 py-0.5 text-red-800 hover:bg-red-50 disabled:opacity-50"
                >
                  <Square className="h-3 w-3 fill-current" />
                  Stop
                </button>
              </div>
            ) : null}
            {showCashDrawer ? (
            <button
              type="button"
              onClick={() => setCashOpen(true)}
              className={[
                'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold',
                cashQuery.data
                  ? 'bg-green-100 text-green-800 ring-1 ring-green-200'
                  : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-600',
              ].join(' ')}
            >
              {cashQuery.data
                ? `💵 ${formatCurrency(cashQuery.data.summary.expectedCash)}`
                : '💵 No Session'}
            </button>
          ) : null}
            <StoreSwitcher />
            <UserMenuDropdown storeName={storeName} />
          </div>
        </header>

        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
      <CashDrawerModal open={cashOpen} onClose={() => setCashOpen(false)} />
      <ChecklistSlideOver
        open={checklistOpen}
        onClose={() => setChecklistOpen(false)}
        initialType={checklistType}
      />
      <CommandPalette
        open={quoteSearchOpen}
        onClose={() => setQuoteSearchOpen(false)}
        onOpenCash={() => setCashOpen(true)}
      />
    </div>
  );
}
